import { ADMIN_API_BASE_URL } from "../config";
import { handleApiResponse } from "../utils/commonfunction";
import {
  makeApiRequest,
  addAppTypeToParams,
  addAppTypeToBody,
} from "../utils/apiHelper";

export interface User {
  id: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  role: string;
  status: "active" | "inactive" | "pending";
  created_at: string;
  last_login?: string;
}

export interface CreateUserData {
  full_name: string;
  email: string;
  role: string;
  status: "active" | "inactive" | "pending";
  password: string;
}

export interface UpdateUserData {
  full_name?: string;
  email?: string;
  role?: string;
  status?: "active" | "inactive" | "pending";
}

export interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  stats?: {
    active_users: number;
    admin_role_users: number;
    inactive_users: number;
    total_users: number;
  };
}
interface UserLog {
  id: string;
  user_id: string;
  user_email?: string;
  user_mobile?: string;
  endpoint: string;
  method: string;
  request_data: any;
  response_status: number;
  response_data: any;
  ip_address?: string;
  user_agent?: string;
  timestamp: string;
  execution_time_ms?: number;
  success: boolean;
  error_message?: string;
}
export interface UserLogsData {
  user: {
    id: string;
    full_name: string;
    email: string;
    mobile_number?: string;
  };
  logs: UserLog[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
  summary: {
    total_requests: number;
    successful_requests: number;
    failed_requests: number;
    avg_execution_time: number;
    success_rate: number;
    last_activity?: string;
  };
}
export interface UserStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  pending_users: number;
  role_distribution: {
    "Super Admin": number;
    Admin: number;
    Editor: number;
    Viewer: number;
  };
}

export interface AppUser {
  id: string;
  full_name: string;
  email: string;
  mobile?: string;
  kyc_status?: "verified" | "pending" | "rejected" | "not_submitted";
  wallet_balance?: number;
  deposit_balance?: number;
  winnings_balance?: number;
  withdrawable_balance?: number;
  account_status: "active" | "inactive" | "pending";
  last_login?: string;
  created_at: string;
  date_of_birth?: string;
  profile_picture?: string;
  total_challenges_joined?: number;
  total_challenges_won?: number;
  kyc_documents?: {
    id: string;
    type: string;
    status: string;
    submitted_at: string;
    url?: string;
  }[];
}

export interface AppUserListResponse {
  users: AppUser[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  stats: any;
}

export interface UpdateAppUserData {
  full_name?: string;
  email?: string;
  mobile?: string;
  status?: "active" | "inactive" | "pending";
  kyc_status?: "verified" | "pending" | "rejected" | "not_submitted";
}

class UserService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async getUsers(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    roleFilter?: string,
    statusFilter?: string
  ): Promise<UserListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...(search && { search }),
      ...(roleFilter && roleFilter !== "all" && { role: roleFilter }),
      ...(statusFilter && statusFilter !== "all" && { status: statusFilter }),
    });

    const paramsWithAppType = addAppTypeToParams(params);
    console.log(
      "🔍 userService: Request params with appType:",
      paramsWithAppType.toString()
    );

    return makeApiRequest(`/users?${paramsWithAppType}`, {
      method: "GET",
    });
  }
  async fetchUserLogs(
    userId: string,
    page: number = 1,
    perPage: number = 20
  ): Promise<UserLogsData> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    const paramsWithAppType = addAppTypeToParams(params);

    return makeApiRequest(`/app-users/${userId}/logs?${paramsWithAppType}`, {
      method: "GET",
    });
  }
  async fetchUserTransactions(
    userId: string,
    page: number = 1,
    perPage: number = 20
  ): Promise<any> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    const paramsWithAppType = addAppTypeToParams(params);

    return makeApiRequest(
      `/app-users/${userId}/wallet-transactions?${paramsWithAppType}`,
      {
        method: "GET",
      }
    );
  }
  async getUserById(userId: string): Promise<User> {
    return makeApiRequest(`/users/${userId}`, {
      method: "GET",
    });
  }

  async createUser(userData: CreateUserData): Promise<User> {
    const bodyWithAppType = addAppTypeToBody(userData);
    return makeApiRequest("/users", {
      method: "POST",
      body: JSON.stringify(bodyWithAppType),
    });
  }

  async updateUser(userId: string, userData: UpdateUserData): Promise<User> {
    const bodyWithAppType = addAppTypeToBody(userData);
    return makeApiRequest(`/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(bodyWithAppType),
    });
  }

  async deleteUser(userId: string): Promise<void> {
    return makeApiRequest(`/users/${userId}`, {
      method: "DELETE",
    });
  }

  async resetUserPassword(
    userId: string,
    newPassword: string
  ): Promise<{ message: string; sessions_invalidated: number }> {
    const bodyWithAppType = addAppTypeToBody({ new_password: newPassword });
    return makeApiRequest(`/users/${userId}/reset-password`, {
      method: "POST",
      body: JSON.stringify(bodyWithAppType),
    });
  }

  async getUserStats(): Promise<UserStats> {
    return makeApiRequest("/users/stats", {
      method: "GET",
    });
  }

  async getRecentActivity(): Promise<any> {
    return makeApiRequest("/dashboard", {
      method: "GET",
    });
  }

  async getDashboardMetrics(): Promise<any> {
    return makeApiRequest("/dashboard/metrics", {
      method: "GET",
    });
  }

  // Mock data for development/testing
  getMockUsers(): User[] {
    return [
      {
        id: "1",
        full_name: "John Doe",
        email: "john.doe@example.com",
        role: "Super Admin",
        status: "active",
        created_at: "2024-01-15T10:30:00Z",
        last_login: "2024-01-20T14:45:00Z",
      },
      {
        id: "2",
        full_name: "Jane Smith",
        email: "jane.smith@example.com",
        role: "Admin",
        status: "active",
        created_at: "2024-01-16T09:15:00Z",
        last_login: "2024-01-19T16:20:00Z",
      },
      {
        id: "3",
        full_name: "Bob Johnson",
        email: "bob.johnson@example.com",
        role: "Editor",
        status: "pending",
        created_at: "2024-01-17T11:00:00Z",
      },
      {
        id: "4",
        full_name: "Alice Brown",
        email: "alice.brown@example.com",
        role: "Viewer",
        status: "inactive",
        created_at: "2024-01-18T13:45:00Z",
        last_login: "2024-01-15T10:30:00Z",
      },
    ];
  }

  // App User Management Methods
  async getAppUsers(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    statusFilter?: string,
    provider?: string,
    kycFilter?: string,
    startDate?: string,
    endDate?: string
  ): Promise<AppUserListResponse> {
    console.log("🔍 userService: getAppUsers called");

    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...(search && { search }),
      ...(statusFilter && { status_filter: statusFilter }),
      ...(provider && { auth_provider: provider }),
      ...(kycFilter && { kyc_filter: kycFilter }),
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
    });

    const paramsWithAppType = addAppTypeToParams(params);
    console.log(
      "🔍 userService: Request params with appType:",
      paramsWithAppType.toString()
    );

    return makeApiRequest(`/app-users?${paramsWithAppType}`, {
      method: "GET",
    });
  }

  async getAppUserById(userId: string): Promise<AppUser> {
    return makeApiRequest(`/app-users/${userId}`, {
      method: "GET",
    });
  }

  async updateAppUser(
    userId: string,
    userData: UpdateAppUserData
  ): Promise<AppUser> {
    const bodyWithAppType = addAppTypeToBody(userData);
    return makeApiRequest(`/app-users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(bodyWithAppType),
    });
  }

  async resetAppUserPassword(
    userId: string,
    newPassword: string
  ): Promise<{ message: string; sessions_invalidated: number }> {
    const bodyWithAppType = addAppTypeToBody({ new_password: newPassword });
    return makeApiRequest(`/app-users/${userId}/reset-password`, {
      method: "POST",
      body: JSON.stringify(bodyWithAppType),
    });
  }

  async exportAuditLogsCsv(
    adminUserId?: string,
    action?: string,
    resourceType?: string,
    startDate?: string,
    endDate?: string
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (adminUserId) params.append("admin_user_id", adminUserId);
    if (action) params.append("action", action);
    if (resourceType) params.append("resource_type", resourceType);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const paramsWithAppType = addAppTypeToParams(params);

    const response = await fetch(
      `${ADMIN_API_BASE_URL}/audit-logs/export-csv?${paramsWithAppType}`,
      {
        method: "GET",
        headers: this.getAuthHeaders(),
      }
    );
    return handleApiResponse(response);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.blob();
  }
}

export const userService = new UserService();
